import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import {
  Phone, Mail, ChevronDown, ChevronRight, ArrowRight, Menu, X,
  Bike, Bolt, Leaf, Coins, CreditCard, GraduationCap, Users, Newspaper, BookOpen, Briefcase,
} from 'lucide-react';

type IconType = React.ComponentType<{ size?: number; className?: string }>;

interface NavLink { name: string; path: string; icon?: IconType; description?: string; badge?: string }
interface MegaGroup { title: string; links: NavLink[] }
interface FeaturedCard { eyebrow: string; title: string; description: string; path: string; cta: string }
interface NavItem {
  name: string;
  path: string;
  megaGroups?: MegaGroup[];
  simpleLinks?: NavLink[];
  featuredCard?: FeaturedCard;
  badge?: string;
  featured?: boolean;
}

const ICON_MAP: Record<string, IconType> = { Phone, Mail, ChevronDown, ChevronRight, ArrowRight, Menu, X, Bike, Bolt, Leaf, Coins, CreditCard, GraduationCap, Users, Newspaper, BookOpen, Briefcase };

function resolveNav(raw: any[]): NavItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => ({
    name: item.name,
    path: item.path,
    badge: item.badge,
    featured: item.featured,
    featuredCard: item.featuredCard,
    megaGroups: Array.isArray(item.megaGroups)
      ? item.megaGroups.map((g: any) => ({ title: g.title, links: (g.links || []).map((l: any) => ({ ...l, icon: l.icon ? ICON_MAP[l.icon] : undefined })) }))
      : undefined,
    simpleLinks: Array.isArray(item.simpleLinks)
      ? item.simpleLinks.map((l: any) => ({ ...l, icon: l.icon ? ICON_MAP[l.icon] : undefined }))
      : undefined,
  }));
}

const DEFAULT_NAV: NavItem[] = [
  {
    name: 'Produits',
    path: '/products',
    megaGroups: [
      {
        title: 'Motos électriques',
        links: [
          { name: 'SUNU', path: '/products/sunu', icon: Bike, description: 'Urbain · livraison · 100 km autonomie', badge: 'Pro' },
          { name: 'Teranga+', path: '/products/teranga-plus', icon: Bike, description: 'Citadine accessible · Pay Molo 24 mois', badge: 'Populaire' },
          { name: 'Omega', path: '/products/omega', icon: Bike, description: 'TukTuk électrique · cargo & rural', badge: 'Rural' },
        ],
      },
      {
        title: 'Découvrir',
        links: [{ name: 'Toute la gamme', path: '/products', icon: ArrowRight, description: 'Comparer tous les modèles' }],
      },
    ],
    featuredCard: {
      eyebrow: 'Programme phare',
      title: 'Proxy Fleet',
      description: 'Investissez dans une flotte de motos électriques au Sénégal. Suivi temps réel via FLIT.',
      path: '/proxy-fleet',
      cta: 'Découvrir le programme',
    },
  },
  {
    name: 'Solutions',
    path: '/services',
    megaGroups: [
      {
        title: 'Énergie',
        links: [
          { name: 'Stations Proxy Power', path: '/stations', icon: Bolt, description: '4 stations solaires actives' },
          { name: 'Calculateur CO₂', path: '/emission-calculator', icon: Leaf, description: 'Mesurez votre impact' },
        ],
      },
      {
        title: 'Financement',
        links: [
          { name: 'PayGo', path: '/pay-as-you-go', icon: Coins, description: 'À partir de 2 000 FCFA/jour' },
          { name: 'Pay Molo', path: '/products/teranga-plus#pay-molo', icon: CreditCard, description: 'Crédit Teranga+ 24 mois' },
        ],
      },
      {
        title: 'Formation',
        links: [{ name: 'Proxy Academy', path: '/academy', icon: GraduationCap, description: 'Devenir conducteur·rice pro' }],
      },
    ],
    featuredCard: {
      eyebrow: 'Investisseurs diaspora',
      title: 'Proxy Fleet',
      description: "Possédez une moto électrique au Sénégal, on s'occupe de tout. Revenus mensuels suivis temps réel.",
      path: '/proxy-fleet',
      cta: 'En savoir plus',
    },
  },
  { name: 'Investir', path: '/invest' },
  {
    name: 'Société',
    path: '/about',
    simpleLinks: [
      { name: 'À propos', path: '/about', icon: Users, description: 'Notre histoire & équipe' },
      { name: 'Newsroom', path: '/newsroom', icon: Newspaper, description: 'Actualités & presse' },
      { name: 'Blog · Insights', path: '/blog', icon: BookOpen, description: 'Analyses & retours terrain' },
      { name: 'Carrières', path: '/careers', icon: Briefcase, description: 'Rejoignez Proxy' },
      { name: 'Contact', path: '/contact', icon: Mail, description: 'Nous écrire' },
    ],
  },
];

const HeaderModern: React.FC = () => {
  const location = useLocation();
  const settings = useSiteSettings();
  const email = settings?.email || 'hello@proxyemobility.com';
  const topBarNote = settings?.top_bar_note || 'Mobilité électrique solaire · Sénégal';
  const NAV = useMemo(() => {
    const raw = (settings as any)?.nav;
    return Array.isArray(raw) && raw.length ? resolveNav(raw) : DEFAULT_NAV;
  }, [settings]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const openDropdown = useCallback((name: string) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setActiveDropdown(name);
  }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setActiveDropdown(null), 300);
  }, []);

  const isActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(path + '/'));
  const isItemActive = (item: NavItem) => {
    if (item.path && isActive(item.path)) return true;
    if (item.megaGroups) return item.megaGroups.some((g) => g.links.some((l) => location.pathname === l.path || location.pathname.startsWith(l.path + '/')));
    if (item.simpleLinks) return item.simpleLinks.some((l) => location.pathname === l.path || location.pathname.startsWith(l.path + '/'));
    return false;
  };

  const dropdownItem = activeDropdown ? NAV.find((i) => i.name === activeDropdown) : null;
  const panelTop = scrolled ? 56 : 104;

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 w-full bg-white"
        style={{
          zIndex: 100,
          boxShadow: scrolled ? '0 4px 24px -12px rgba(0,0,0,0.12)' : 'none',
          paddingTop: 'env(safe-area-inset-top)',
          borderBottom: scrolled ? '0' : '0.5px solid rgba(0,0,0,0.06)',
        }}
      >
        {/* Top bar */}
        <div className={`hidden md:block bg-neutral-900 text-white text-xs transition-all duration-300 overflow-hidden ${scrolled ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'}`}>
          <div className="mx-auto max-w-7xl px-4 lg:px-6 py-1.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-[11px]">
              <a href={`mailto:${email}`} className="hidden lg:inline-flex items-center gap-1.5 hover:text-primary-400 transition-colors">
                <Mail size={11} /> {email}
              </a>
            </div>
            <span className="text-[10px] tracking-[0.15em] uppercase text-white/60">{topBarNote}</span>
          </div>
        </div>

        {/* Main bar */}
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <div className={`flex items-center justify-between gap-4 transition-all duration-300 ${scrolled ? 'h-14' : 'h-16 md:h-18'}`}>
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0" aria-label="Proxy Emobility · Accueil">
              <img src="/proxy-logo.png" alt="Proxy Emobility" className={`w-auto transition-all duration-300 group-hover:scale-105 ${scrolled ? 'h-7' : 'h-8 md:h-10'}`} />
            </Link>

            {/* Desktop nav (lg+) */}
            <div ref={navRef} className="hidden lg:flex items-center gap-1 flex-1 justify-center" onMouseLeave={scheduleClose}>
              {NAV.map((item) => {
                const active = isItemActive(item);
                const hasMenu = !!item.megaGroups || !!item.simpleLinks;
                const open = activeDropdown === item.name;
                return hasMenu ? (
                  <div key={item.name} className="relative flex items-center" onMouseEnter={() => openDropdown(item.name)}>
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(open ? null : item.name)}
                      className={`px-3.5 py-2 text-sm font-bold transition-colors rounded-xl inline-flex items-center gap-1.5 ${open || active ? 'text-primary-700 bg-primary-50/60' : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50'}`}
                      aria-expanded={open}
                      aria-haspopup="true"
                    >
                      {item.name}
                      <ChevronDown size={13} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`px-3.5 py-2 text-sm font-bold transition-all rounded-xl inline-flex items-center gap-1.5 ${item.featured ? 'text-neutral-900 hover:text-primary-700' : active ? 'text-primary-700' : 'text-neutral-700 hover:text-neutral-900'}`}
                  >
                    {item.name}
                    {item.badge && <span className="text-[9px] font-bold uppercase tracking-[0.12em] bg-neutral-900 text-amber-300 px-1.5 py-0.5 rounded">{item.badge}</span>}
                  </Link>
                );
              })}
            </div>

            {/* Tablet nav (md..lg) */}
            <nav className="hidden md:flex lg:hidden items-center gap-1 flex-1 justify-center">
              {NAV.slice(0, 3).map((item) => {
                const active = isItemActive(item);
                return (
                  <Link key={item.name} to={item.path} className={`px-3 py-1.5 text-[13px] font-bold rounded-lg transition-colors ${active ? 'text-primary-700 bg-primary-50/60' : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50'}`}>
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to="/proxy-fleet"
                className="hidden lg:inline-flex items-center gap-1.5 rounded-xl bg-[#D4A574] px-4 py-2.5 text-xs font-bold text-[#0A1628] transition-all hover:-translate-y-0.5 hover:bg-[#c79a63] hover:shadow-[0_10px_24px_-8px_rgba(196,165,116,0.7)]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A1628] animate-pulse" />
                FLEET
              </Link>
              <Link to="/proxy-fleet" className="lg:hidden inline-flex items-center gap-1.5 rounded-lg bg-[#D4A574] px-3 py-2 text-[11px] font-bold text-[#0A1628]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A1628]" />
                FLEET
              </Link>
              <Link to="/contact" className="hidden lg:inline-flex items-center gap-1.5 rounded-xl bg-neutral-900 px-4 py-2.5 text-xs font-bold text-white hover:bg-primary-700 transition-all hover:-translate-y-0.5 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.3)]">
                Nous contacter
                <ArrowRight size={13} />
              </Link>
              <button type="button" onClick={() => setMobileOpen(true)} aria-label="Ouvrir le menu" className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100 transition-colors">
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop dropdown backdrop */}
      <div
        className={`fixed inset-0 transition-opacity duration-300 lg:hidden ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(0,0,0,0.4)', zIndex: 98 }}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* Desktop mega dropdown panel */}
      {dropdownItem &&
        (() => {
          const item = dropdownItem;
          const hasMega = !!item.megaGroups;
          const hasFeatured = !!item.featuredCard;
          const hasSimple = !!item.simpleLinks;
          return (
            <div className="hidden lg:block">
              <div className="fixed left-0 right-0 bottom-0" style={{ top: panelTop, zIndex: 190 }} onClick={() => setActiveDropdown(null)} aria-hidden="true" />
              <div className="fixed left-0 right-0" style={{ top: panelTop, zIndex: 200 }} onMouseEnter={() => openDropdown(item.name)} onMouseLeave={scheduleClose}>
                <div className="mx-auto max-w-7xl px-4 lg:px-6 pt-2">
                  <div className="bg-white rounded-2xl border border-neutral-200 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] overflow-hidden">
                    {hasMega && (
                      <div className={`grid gap-6 p-6 lg:p-8 ${hasFeatured ? 'grid-cols-[1.2fr_1fr_1fr_1fr]' : 'grid-cols-3'}`}>
                        {item.megaGroups!.map((group) => (
                          <div key={group.title}>
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400 mb-3">{group.title}</div>
                            <div className="space-y-1">
                              {group.links.map((link) => {
                                const Icon = link.icon;
                                return (
                                  <Link key={link.path} to={link.path} className="group flex items-start gap-3 p-2.5 -mx-2.5 rounded-lg hover:bg-neutral-50 transition-colors" onClick={() => setActiveDropdown(null)}>
                                    {Icon && (
                                      <div className="w-9 h-9 rounded-lg bg-primary-50 group-hover:bg-primary-100 flex items-center justify-center flex-shrink-0 transition-colors">
                                        <Icon size={16} className="text-primary-700" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0 pt-0.5">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-neutral-900 group-hover:text-primary-700 transition-colors">{link.name}</span>
                                        {link.badge && <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{link.badge}</span>}
                                      </div>
                                      {link.description && <div className="text-[11px] text-neutral-500 leading-snug mt-0.5">{link.description}</div>}
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {hasFeatured && item.featuredCard && (
                          <Link to={item.featuredCard.path} onClick={() => setActiveDropdown(null)} className="bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] rounded-xl p-5 text-white hover:from-[#0F1E36] hover:to-[#264a78] transition-all group flex flex-col justify-between">
                            <div>
                              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#D4A574] mb-2">{item.featuredCard.eyebrow}</div>
                              <div className="text-lg font-black text-white mb-1.5">{item.featuredCard.title}</div>
                              <p className="text-[11px] text-white/70 leading-relaxed line-clamp-3">{item.featuredCard.description}</p>
                            </div>
                            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#D4A574] group-hover:gap-2 transition-all">
                              {item.featuredCard.cta}
                              <ArrowRight size={12} />
                            </div>
                          </Link>
                        )}
                      </div>
                    )}
                    {hasSimple && (
                      <div className="p-3 max-w-md">
                        {item.simpleLinks!.map((link) => {
                          const Icon = link.icon;
                          return (
                            <Link key={link.path} to={link.path} onClick={() => setActiveDropdown(null)} className="group flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-50 transition-colors">
                              {Icon && (
                                <div className="w-8 h-8 rounded-lg bg-primary-50 group-hover:bg-primary-100 flex items-center justify-center flex-shrink-0 transition-colors">
                                  <Icon size={14} className="text-primary-700" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0 pt-0.5">
                                <div className="text-sm font-bold text-neutral-900 group-hover:text-primary-700 transition-colors">{link.name}</div>
                                {link.description && <div className="text-[11px] text-neutral-500 mt-0.5">{link.description}</div>}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[400px] bg-white shadow-2xl transition-transform duration-300 ease-out lg:hidden flex flex-col ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ zIndex: 99, paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navigation"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
            <img src="/proxy-logo.png" alt="Proxy" className="h-8 w-auto" />
          </Link>
          <button type="button" onClick={() => setMobileOpen(false)} aria-label="Fermer le menu" className="inline-flex h-10 w-10 items-center justify-center rounded-xl hover:bg-neutral-100 transition-colors">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Navigation mobile">
          {NAV.map((item) => {
            const hasMenu = !!item.megaGroups || !!item.simpleLinks;
            const expanded = mobileExpanded === item.name;
            const active = isItemActive(item);
            return hasMenu ? (
              <div key={item.name} className="mb-1">
                <button
                  type="button"
                  onClick={() => setMobileExpanded(expanded ? null : item.name)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-[15px] font-bold transition-colors ${expanded ? 'bg-neutral-50 text-neutral-900' : active ? 'bg-primary-50 text-primary-700' : 'text-neutral-900 hover:bg-neutral-50'}`}
                  aria-expanded={expanded}
                >
                  {item.name}
                  <ChevronDown size={16} className={`text-neutral-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                </button>
                {expanded && (
                  <div className="mt-1 mb-2 pl-2">
                    {item.megaGroups?.map((group) => (
                      <div key={group.title} className="mb-3 last:mb-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400 px-3 mb-1">{group.title}</div>
                        {group.links.map((link) => {
                          const Icon = link.icon;
                          return (
                            <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)} className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-neutral-50 transition-colors">
                              {Icon && (
                                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Icon size={14} className="text-primary-700" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-[14px] font-bold text-neutral-900">{link.name}</span>
                                  {link.badge && <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{link.badge}</span>}
                                </div>
                                {link.description && <div className="text-[11px] text-neutral-500 leading-snug mt-0.5">{link.description}</div>}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                    {item.simpleLinks?.map((link) => {
                      const Icon = link.icon;
                      return (
                        <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-50 transition-colors">
                          {Icon && (
                            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                              <Icon size={14} className="text-primary-700" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-bold text-neutral-900">{link.name}</div>
                            {link.description && <div className="text-[11px] text-neutral-500 mt-0.5">{link.description}</div>}
                          </div>
                        </Link>
                      );
                    })}
                    {item.featuredCard && (
                      <Link to={item.featuredCard.path} onClick={() => setMobileOpen(false)} className="mt-3 mx-3 block p-4 rounded-xl bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] text-white">
                        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#D4A574] mb-1">{item.featuredCard.eyebrow}</div>
                        <div className="text-sm font-black mb-1">{item.featuredCard.title}</div>
                        <p className="text-[11px] text-white/70 leading-relaxed">{item.featuredCard.description}</p>
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-[#D4A574]">
                          {item.featuredCard.cta} <ArrowRight size={11} />
                        </div>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-[15px] font-bold transition-colors ${active ? 'bg-primary-50 text-primary-700' : 'text-neutral-900 hover:bg-neutral-50'}`}
              >
                {item.name}
                <ChevronRight size={16} className="text-neutral-400" />
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-neutral-200 px-4 py-4 space-y-2 bg-neutral-50">
          <Link to="/proxy-fleet" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#D4A574] text-[#0A1628] text-sm font-bold hover:bg-[#c79a63] transition-colors">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0A1628]" />
            FLEET
          </Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-neutral-900 text-white text-sm font-bold hover:bg-primary-700 transition-colors">
            Nous contacter
            <ArrowRight size={14} />
          </Link>
          <div className="grid grid-cols-1 gap-2 pt-1">
            <a href="mailto:hello@proxyemobility.com" className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white border border-neutral-200 text-[12px] font-bold text-neutral-700 hover:border-neutral-300 transition-colors">
              <Mail size={12} /> Écrire
            </a>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className={`transition-all duration-300 ${scrolled ? 'h-14' : 'h-16 md:h-[6.5rem]'}`} aria-hidden="true" />
    </>
  );
};

export default HeaderModern;
